// reviews-schema.util.ts
export type ReviewKey = `review_${number}`;

export type RawReviews = Record<
  ReviewKey,
  {
    id: number;
    // "review_1_name", "review_1_rating", ...
    [k: string]: string | number;
  }
>;

export type OrgInfo = {
  id: string;   // e.g. "https://youngsparrows.com/"
  name: string; // e.g. "Young Sparrows"
  url: string;  // e.g. "https://youngsparrows.com/"
  logo: string; // e.g. "https://youngsparrows.com/logo.webp"
};

type BuiltSchema = Array<Record<string, any>>;

const toISODate = (d = new Date()) => d.toISOString().slice(0, 10);

type DatesArg =
  | Array<string>
  | Record<number, string>
  | ((indexZeroBased: number, reviewId: number) => string);

function pickDate(dates: DatesArg | undefined, index: number, id: number): string {
  if (!dates) return toISODate();
  if (Array.isArray(dates)) return dates[index] || toISODate();
  if (typeof dates === "function") return dates(index, id) || toISODate();
  // Record<number, string>
  return dates[id] || toISODate();
}

export function buildReviewsSchema(opts: {
  org: OrgInfo;
  reviews: RawReviews;
  dates?: DatesArg;
}): BuiltSchema {
  const { org, reviews, dates } = opts;

  // Sortiraj po review.id (fallback: suffix iz ključa)
  const entries = Object.keys(reviews)
    .filter((k): k is ReviewKey => /^review_\d+$/.test(k))
    .sort((a, b) => {
      const A = reviews[a]?.id ?? Number(a.split("_")[1]);
      const B = reviews[b]?.id ?? Number(b.split("_")[1]);
      return Number(A) - Number(B);
    });

  const list = entries.map((key, i) => {
    const item = reviews[key]!;
    const id = Number(item.id ?? String(key).split("_")[1]);

    const name = (item[`${key}_name`] as string) || "";
    const author = (item[`${key}_author`] as string) || name;
    const quote = (item[`${key}_quote`] as string) || "";

    const ratingNumRaw = item[`${key}_rating`];
    const ratingNum = Number(ratingNumRaw);
    const ratingValue = Number.isFinite(ratingNum) ? ratingNum : 5;

    const datePublished = (item[`${key}_datePublished`] as string) || "";

    // const datePublished = pickDate(dates, i, id);

    const baseUrl = org.url.replace(/\/$/, "");
    const reviewAnchor = `${baseUrl}/#review-${id}`;

    return {
      "@type": "Review",
      "@id": reviewAnchor,
      "url": reviewAnchor,
      "itemReviewed": { "@id": org.id },
      "name": name, // može ostati prazan ili koristiti skraćeni citat
      "datePublished": datePublished,
      "reviewBody": quote,
      "reviewRating": { "@type": "Rating", "ratingValue": ratingValue, "bestRating": 5 },
      "author": { "@type": "Person", "name": author }
    };
  });

  const ratings = list.map(r => Number(r.reviewRating?.ratingValue) || 0);
  const reviewCount = ratings.length;
  const ratingCount = reviewCount;
  const avg = reviewCount
    ? Math.round((ratings.reduce((a, b) => a + b, 0) / reviewCount) * 10) / 10
    : 0;

  return [
    {
      "@context": "https://schema.org",
      "@type": "Preschool",
      "@id": org.id,
      "name": org.name,
      "url": org.url,
      "logo": org.logo,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": avg,
        "reviewCount": reviewCount,
        "ratingCount": ratingCount
      },
      "review": list
    }
  ];
}
